/**
 * 16x16 item, skill and UI icons. Generated procedurally on first use and cached.
 * Equipment icons are tinted by `tier` (0 wood/rust, 1 iron, 2 steel, 3 sky crystal,
 * 4 ember, 5 aether); `tier` is ignored for every other icon.
 */
import { memo, type Canvas } from './core';
import type { IconId } from './types';
import { Grid } from './env/raster';
import { amulet, armor, boots, dagger, greatsword, helm, ring, staff, sword } from './icons/equip';
import { belt, bracers, charm, gloves, legs, shoulders } from './icons/gear-extra';
import { bow, crossbow } from './icons/ranged';
import { focus, shield } from './icons/offhand';
import {
  bracersCloth,
  hoodCloth,
  legsCloth,
  mantleCloth,
  robeCloth,
  sashCloth,
  slippersCloth,
  wrapsCloth,
} from './icons/armor-cloth';
import {
  beltLeather,
  bootsLeather,
  bracersLeather,
  capLeather,
  glovesLeather,
  jerkinLeather,
  legsLeather,
  spauldersLeather,
} from './icons/armor-leather';
import {
  breastplatePlate,
  cuissesPlate,
  gauntletPlate,
  girdlePlate,
  greavesPlate,
  helmPlate,
  pauldronsPlate,
  vambracePlate,
} from './icons/armor-plate';
import { ELEMENT_ICONS } from './icons/elements';
import { gem, type GemArtType } from './icons/gems';
import { material } from './icons/materials';
import { ITEM_ICONS } from './icons/items';
import { SKILL_ICONS } from './icons/skills';
import { EMBLEM_ICONS } from './icons/emblems';
import { UI_ICONS } from './icons/ui';

const EQUIP: Partial<Record<IconId, (tier: number) => Grid>> = {
  icon_sword: (t) => sword(material(t)),
  icon_greatsword: (t) => greatsword(material(t)),
  icon_dagger: (t) => dagger(material(t)),
  icon_staff: (t) => staff(material(t), t),
  icon_helm: (t) => helm(material(t)),
  icon_armor: (t) => armor(material(t)),
  icon_boots: (t) => boots(material(t)),
  icon_ring: (t) => ring(material(t)),
  icon_amulet: (t) => amulet(material(t)),
  icon_gloves: (t) => gloves(material(t)),
  icon_belt: (t) => belt(material(t)),
  icon_charm_small: (t) => charm(material(t), 'small'),
  icon_charm_large: (t) => charm(material(t), 'large'),
  icon_charm_grand: (t) => charm(material(t), 'grand'),
  icon_bow: (t) => bow(material(t), t),
  icon_crossbow: (t) => crossbow(material(t), t),
  icon_shoulders: (t) => shoulders(material(t)),
  icon_bracers: (t) => bracers(material(t)),
  icon_legs: (t) => legs(material(t)),
  icon_shield: (t) => shield(material(t), t),
  icon_focus: (t) => focus(material(t), t),
  // armor types (the plain helm/armor/gloves/boots/belt/shoulders/bracers/legs above are mail)
  icon_helm_cloth: (t) => hoodCloth(material(t), t),
  icon_armor_cloth: (t) => robeCloth(material(t), t),
  icon_gloves_cloth: (t) => wrapsCloth(material(t), t),
  icon_boots_cloth: (t) => slippersCloth(material(t), t),
  icon_belt_cloth: (t) => sashCloth(material(t), t),
  icon_shoulders_cloth: (t) => mantleCloth(material(t), t),
  icon_bracers_cloth: (t) => bracersCloth(material(t), t),
  icon_legs_cloth: (t) => legsCloth(material(t), t),
  icon_helm_leather: (t) => capLeather(material(t), t),
  icon_armor_leather: (t) => jerkinLeather(material(t), t),
  icon_gloves_leather: (t) => glovesLeather(material(t), t),
  icon_boots_leather: (t) => bootsLeather(material(t), t),
  icon_belt_leather: (t) => beltLeather(material(t), t),
  icon_shoulders_leather: (t) => spauldersLeather(material(t), t),
  icon_bracers_leather: (t) => bracersLeather(material(t), t),
  icon_legs_leather: (t) => legsLeather(material(t), t),
  icon_helm_plate: (t) => helmPlate(material(t)),
  icon_armor_plate: (t) => breastplatePlate(material(t)),
  icon_gloves_plate: (t) => gauntletPlate(material(t)),
  icon_boots_plate: (t) => greavesPlate(material(t)),
  icon_belt_plate: (t) => girdlePlate(material(t)),
  icon_shoulders_plate: (t) => pauldronsPlate(material(t)),
  icon_bracers_plate: (t) => vambracePlate(material(t)),
  icon_legs_plate: (t) => cuissesPlate(material(t)),
};

const OTHER: Partial<Record<IconId, () => Grid>> = {
  ...ITEM_ICONS,
  ...SKILL_ICONS,
  ...EMBLEM_ICONS,
  ...UI_ICONS,
  ...ELEMENT_ICONS,
};

const clampTier = (t: number | undefined) =>
  Math.max(0, Math.min(5, Math.floor(Number.isFinite(t) ? (t as number) : 0)));

/** Build an icon's pixel grid (uncached). Exported for tests; use getIcon for drawing. */
export function buildIcon(id: IconId, tier: number): Grid {
  const eq = EQUIP[id];
  if (eq) return eq(tier);
  const other = OTHER[id];
  if (other) return other();
  if (id.startsWith('icon_gem_')) {
    const [, , type, q] = id.split('_');
    return gem(type as GemArtType, Number(q));
  }
  return new Grid(16, 16).rect(4, 4, 8, 8, '#ff00ff');
}

/** tier (0-5) tints equipment icons by material; ignored for other icons. */
export const getIcon: (id: IconId, tier?: number) => Canvas = memo(
  (id, tier) => `${id}|${EQUIP[id] ? clampTier(tier) : 0}`,
  (id, tier) => buildIcon(id, EQUIP[id] ? clampTier(tier) : 0).toCanvas(),
);
